import { ApplicationConfig, importProvidersFrom, isDevMode, APP_INITIALIZER, inject, ErrorHandler } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { TraceService } from '@sentry/angular';
import { Router } from '@angular/router';
import { SentryContextService } from '@core/services/sentry-context.service';
import { provideRouter, withEnabledBlockingInitialNavigation, withInMemoryScrolling, withPreloading, withNavigationErrorHandler, RouteReuseStrategy } from '@angular/router';
import { PageReuseStrategy } from '@core/navigation-state';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptorsFromDi, withFetch } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';

import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ApiPayloadInterceptor } from '@core/services/api-payload.interceptor';
import { ClientConfigInterceptor } from '@core/interceptors/client-config.interceptor';
import { AuthInterceptor } from '@core/interceptors/auth.interceptor';
import { ApiUnauthorisedInterceptor } from '@core/interceptors/api-unauthorised.interceptor';
import { LoadingInterceptor } from '@core/interceptors/loading.interceptor';
import { ErrorHandlerInterceptor } from '@core/interceptors/error-handler.interceptor';
import { CacheInterceptor } from '@core/interceptors/cache.interceptor';
import { SelectivePreloadStrategy } from '@core/services/selective-preload.strategy';

// NEW: Authentication Services
import { AuthStateService } from '@core/services/auth-state.service';
import { CompanyConfigService } from '@core/services/company-config.service';
import { TokenService } from '@core/services/token.service';
import { SessionManagementService } from '@core/services/session-management.service';
import { DialogService } from '@core/services/dialog.service';

// State Preservation Services
import { NavigationInterceptorService } from '@core/state-preservation/services/navigation-interceptor.service';
import { StateSnapshotService } from '@core/state-preservation/services/state-snapshot.service';
import { StateStorageService } from '@core/state-preservation/services/state-storage.service';

// NgRx
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideRouterStore } from '@ngrx/router-store';

// Routes
import { routes } from './app.routes';
import {

  withNgxWebstorageConfig,
  withLocalStorage,
  withSessionStorage
} from 'ngx-webstorage';
// Store
import { metaReducers, reducers } from '@core/store';
import { provideNgxWebstorage, LocalStorageService } from 'ngx-webstorage';
import { DBConfig, provideIndexedDb } from 'ngx-indexed-db';
import { provideDynamicFormSystem } from '@shared/components/dynamic-common-form/dynamic-form-initializer';

const combinedDBConfig: DBConfig = {
  name: "CombinedDB",
  // Bumped from 3 → 4 so onupgradeneeded fires on all existing devices and
  // recreates any missing object stores (e.g. resource_categories missing on
  // low-end devices where the v3 schema write was interrupted).
  version: 4,
  objectStoresMeta: [
    {
      store: "resource_categories",
      storeConfig: { keyPath: "Name", autoIncrement: false },
      storeSchema: [],
    },
    {
      store: "unity_searches",
      storeConfig: { keyPath: "Name", autoIncrement: true },
      storeSchema: [
        {
          name: "searchTerm",
          keypath: "searchTerm",
          options: { unique: false },
        },
        { name: "activeTab", keypath: "activeTab", options: { unique: false } },
        { name: "items", keypath: "items", options: { unique: false } },
        { name: "timestamp", keypath: "timestamp", options: { unique: false } },
      ],
    },
    {
      store: "mingle_searches",
      storeConfig: { keyPath: "Name", autoIncrement: true },
      storeSchema: [
        {
          name: "searchTerm",
          keypath: "searchTerm",
          options: { unique: false },
        },
        { name: "activeTab", keypath: "activeTab", options: { unique: false } },
        { name: "items", keypath: "items", options: { unique: false } },
        { name: "timestamp", keypath: "timestamp", options: { unique: false } },
      ],
    },
  ],
};
export const appConfig: ApplicationConfig = {
  providers: [
    // NEW: Authentication Services (REQUIRED)
    AuthStateService,
    CompanyConfigService,
    TokenService,
    SessionManagementService,
    DialogService,

    // State Preservation Services (Automatic state management during navigation)
    StateStorageService,
    StateSnapshotService,
    NavigationInterceptorService,
    {
      provide: APP_INITIALIZER,
      useFactory: (navInterceptor: NavigationInterceptorService) => () => {
        // Initialize navigation interceptor on app startup
        console.log('[App] State preservation system initialized');
        return Promise.resolve();
      },
      deps: [NavigationInterceptorService],
      multi: true
    },


    // IndexedDB Configuration
    provideIndexedDb(combinedDBConfig),

    // Dynamic Form System
    provideDynamicFormSystem(),

    // Router with Performance Optimizations (FIXED: Removed duplicate provideRouter)
    provideRouter(
      routes,
      withEnabledBlockingInitialNavigation(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled'
      }),
      withPreloading(SelectivePreloadStrategy), // PERFORMANCE: Selective preloading
      withNavigationErrorHandler((e: any) => {
        const err = e?.error ?? e;
        const msg = err?.message || '';
        if (err?.name === 'ChunkLoadError' ||
            msg.includes('Loading chunk') ||
            msg.includes('ChunkLoadError')) {
          console.warn('[Router] ChunkLoadError detected — reloading page to fetch updated chunks');
          window.location.reload();
        }
      })
    ),

    // Override Angular's DefaultRouteReuseStrategy for back-navigation state preservation.
    // MUST be after provideRouter — Angular DI "last wins" for the same token.
    { provide: RouteReuseStrategy, useClass: PageReuseStrategy },

    // Platform
    provideClientHydration(),
    provideAnimationsAsync(),
    
    // HTTP
    provideHttpClient(
      withInterceptorsFromDi(),
      withFetch()
    ),

    // HTTP Interceptors (Order matters: Loading -> Cache -> ClientConfig -> Auth -> Payload -> Unauthorised -> ErrorHandler)
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true
    },
    // CacheInterceptor sits after Loading (loader still shows on first miss) but before
    // Auth/ClientConfig so cached responses are served without re-stamping headers.
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CacheInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ClientConfigInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiPayloadInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiUnauthorisedInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlerInterceptor,
      multi: true
    },
    
    // NgRx Store
    provideStore(reducers, { metaReducers }),
    provideEffects([]),
    provideRouterStore(),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      autoPause: true,
      trace: false,
      traceLimit: 75,
      connectInZone: true
    }),

    // Ngx Webstorage (FIXED: Removed duplicate, kept full configuration)
    provideNgxWebstorage(
      withNgxWebstorageConfig({
        prefix: "tibss",
        separator: ".",
        caseSensitive: true,
      }),
      withLocalStorage(),   // enables @LocalStorage({ strategy: 'local' })
      withSessionStorage()  // enables @SessionStorage({ strategy: 'session' })
    ),
    { provide: LocalStorageService, useClass: LocalStorageService },

    // Service Worker (PERFORMANCE: Reduced registration delay from 30s to 10s)
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:10000'
    }),

    // ─── Sentry: disabled for demo branch ───────────────────────────────────
    // { provide: ErrorHandler, useValue: Sentry.createErrorHandler({ showDialog: false }) },
    // { provide: TraceService, useClass: TraceService, deps: [Router] },
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: () => () => {},
    //   deps: [TraceService],
    //   multi: true
    // },
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: (s: SentryContextService) => () => {},
    //   deps: [SentryContextService],
    //   multi: true
    // },
    // ──────────────────────────────────────────────────────────────────────────
  ],
};
